import React, { ReactElement } from "react";
import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ActionBar, WeekPicker } from "../../components";
import { gqlQuery } from "../../lib";
import { RangePicker, YearPicker } from "../DatePickers";
import { SearchField } from "../OptionField";
import { MonthPicker } from "../DatePickers/MonthPicker";
import { countryOptions } from "../../lib/utils";
import { Country } from "../../API";

type FilterProps<T> = {
  value: T;
  onChange: (value: T) => void;
  type?: "week" | "range" | "month" | "year";
  InputIcon?: ReactElement;
  inputFormat?: string;
};

type Props = {
  testID: string;
  company?: FilterProps<string>;
  employee?: FilterProps<string>;
  dates?: FilterProps<Interval | null>;
  country?: FilterProps<string>;
};

export const FilterBar = ({
  testID,
  company,
  employee,
  dates,
  country,
}: Props) => {
  const { data: companies } = useQuery(
    gqlQuery({
      req: "listCompanies",
      vars: {
        limit: 1000,
      },
    })
  );
  const { data: employees } = useQuery(
    gqlQuery({
      req: "listEmployees",
      vars: {
        limit: 1000,
      },
    })
  );

  const employeeOptions = [
    ...(employees?.data?.listEmployees?.items ?? []).map((i, index) => ({
      label: `${i?.firstName} ${i?.lastName}`,
      value: i?.id ?? "",
      key: i?.id ?? index,
    })),
  ];

  const companyOptions = [
    ...(companies?.data?.listCompanies?.items ?? []).map((i, index) => ({
      label: i?.name ?? "",
      value: i?.id ?? "",
      key: i?.id ?? index,
    })),
  ];

  const DatePicker = ({ type, ...dates }: FilterProps<Interval | null>) =>
    ({
      week: <WeekPicker {...dates} data-testid={`${testID}-weekpicker`} />,
      range: (
        <RangePicker {...dates} data-testid={`${testID}-daterange-picker`} />
      ),
      month: <MonthPicker {...dates} data-testid={`${testID}-month-picker`} />,
      _: null,
      year: <YearPicker {...dates} data-testid={`${testID}-year-picker`} />,
    }[type ?? "_"]);

  return (
    <ActionBar>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            lg: "max-content 400px",
          },
          gap: 2,
        }}
      >
        {dates && <DatePicker {...dates} />}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              md: "repeat(2, 1fr)",
            },
            gap: 2,
          }}
        >
          {employee && (
            <SearchField
              {...employee}
              label="Employee"
              options={employeeOptions}
              data-testid={`${testID}-employee-dropdown`}
            />
          )}
          {company && (
            <SearchField
              {...company}
              label="Company"
              options={companyOptions}
              data-testid={`${testID}-company-dropdown`}
            />
          )}

          {country && (
            <SearchField
              {...country}
              label="Country"
              options={countryOptions}
              data-testid={`${testID}-country-dropdown`}
              onChange={(val) => country.onChange(val as Country)}
            ></SearchField>
          )}
        </Box>
      </Box>
    </ActionBar>
  );
};
